import { Component, Host, h, Element, Prop, Event, EventEmitter } from '@stencil/core';
import { isPlatform } from '@baloise/ds-core';
@Component({
  tag: 'bam-chart-circle-segments',
  shadow: false,
})
export class BamChartCircleSegments {
  @Element() el: HTMLElement;

  @Prop() color: 'yellow' | 'purple' | 'green' | 'red';
  @Prop() values: string;
  @Prop() highlighted: boolean = false;
  @Prop({ mutable: true }) activeItem: number = 1;
  @Prop() disabled: boolean = false;
  @Prop() hasLegend: boolean = false;
  @Prop() data;

  @Event() activeChartItemChanged: EventEmitter<number>;

  private segmentElements: NodeListOf<SVGPathElement>;

  // ############################################################# //
  // ----------------------- Calc Segments ----------------------- //
  // ############################################################# //
  pointOnArc = (center: number[], R: number, angle: number) => {
    const RAD_DEG = Math.PI / 180.0;
    const radians = (angle - 90) * RAD_DEG;

    return [center[0] + R * Math.cos(radians), center[1] + R * Math.sin(radians)];
  };

  setArcDefinition = (center: number[], R: number, start: number, end: number, w: number, corner: number) => {
    const PI2 = Math.PI * 2;
    let points = [];

    let innerRadius = R - w;
    let circumference = Math.abs(end - start);
    corner = Math.min(w / 2, corner);

    if (360 * (corner / (Math.PI * (R - w))) > Math.abs(start - end)) {
      corner = (circumference / 360) * innerRadius * Math.PI;
    }

    // inner and outer radii
    let innerRadius2 = innerRadius + corner;
    let outerRadius = R - corner;

    // corner points
    let innerStart = this.pointOnArc(center, innerRadius2, start);
    let innerEnd = this.pointOnArc(center, innerRadius2, end);

    let outerStart = this.pointOnArc(center, outerRadius, start - 0.6);
    let outerEnd = this.pointOnArc(center, outerRadius, end + 0.6);

    let innerSection = 360 * (corner / (PI2 * innerRadius));
    let outerSection = 360 * (corner / (PI2 * R));

    // arcs endpoints
    let innerArcStart = this.pointOnArc(center, innerRadius, start + innerSection);
    let innerArcEnd = this.pointOnArc(center, innerRadius, end - innerSection);

    let oArcStart = this.pointOnArc(center, R, start - 0.6 + outerSection);
    let oArcEnd = this.pointOnArc(center, R, end + 0.6 - outerSection);

    let innerArcCurve = circumference > 180 + 2 * innerSection ? 1 : 0;
    let outerArcCurve = circumference > 180 + 2 * outerSection ? 1 : 0;

    points = [
      // begin path
      'M',
      outerStart[0],
      outerStart[1],
      // outer start corner
      'A',
      corner,
      corner,
      0,
      0,
      1,
      oArcStart[0],
      oArcStart[1],
      // outer main arc
      'A',
      R,
      R,
      0,
      outerArcCurve,
      1,
      oArcEnd[0],
      oArcEnd[1],
      // outer end corner
      'A',
      corner,
      corner,
      0,
      0,
      1,
      outerEnd[0],
      outerEnd[1],
      // end
      'L',
      innerEnd[0],
      innerEnd[1],
      // inner end corner
      'A',
      corner,
      corner,
      0,
      0,
      1,
      innerArcEnd[0],
      innerArcEnd[1],
      // inner arc
      'A',
      innerRadius,
      innerRadius,
      0,
      innerArcCurve,
      0,
      innerArcStart[0],
      innerArcStart[1],
      // inner start corner
      'A',
      corner,
      corner,
      0,
      0,
      1,
      innerStart[0],
      innerStart[1],
      'Z', // end path
    ];

    return points.join(' ');
  };
  maximizeSegment = (segment: SVGPathElement, ID: number) => {
    const data = this.data.filter(chart => {
      return chart.id === ID;
    });
    segment.setAttribute('d', this.setArcDefinition([0, 0], 180, 0, data[0].angle, 80, 8));
  };

  minimizeSegment = (segment: SVGPathElement, ID: number) => {
    const data = this.data.filter(chart => {
      return chart.id === ID;
    });
    segment.setAttribute('d', this.setArcDefinition([0, 0], 160, 0, data[0].angle, 60, 8));
  };

  updateSegments = () => {
    this.segmentElements.forEach(segment => {
      const segID = parseInt(segment.id.slice(-1));

      segment.classList.remove('is-active');
      this.minimizeSegment(segment, segID);

      if (segID === this.activeItem) {
        segment.classList.add('is-active');
        this.maximizeSegment(segment, segID);
      }
    });
  };
  // ############################################################## //
  // -------------------------- Handlers -------------------------- //
  // ############################################################## //
  handleClick = (ID: number) => {
    this.activeItem = ID;
    this.activeChartItemChanged.emit(this.activeItem);

    this.updateSegments();
  };

  handleHover = (event: MouseEvent, ID: number) => {
    if (this.highlighted && !isPlatform('mobile') && ID !== this.activeItem) {
      switch (event.type) {
        case 'mouseenter':
          this.maximizeSegment(event.target as SVGPathElement, ID);
          break;
        case 'mouseleave':
          this.minimizeSegment(event.target as SVGPathElement, ID);
          break;
        default:
          this.minimizeSegment(event.target as SVGPathElement, ID);
      }
    }
  };

  // ############################################################### //
  // -------------------------- Lifecylce -------------------------- //
  // ############################################################### //
  connectedCallback() {}

  componentDidLoad() {
    this.segmentElements = this.el.querySelectorAll('.bam-chart__segment');
  }

  render() {
    return (
      <Host
        class={{
          'bam-chart-circle-segments': true,
        }}
      >
        <svg height={this.highlighted ? '360' : '320'} width={this.highlighted ? '360' : '320'} viewBox={this.highlighted ? '-180 -180 360 360' : '-160 -160 320 320'}>
          {this.data.map(data => {
            return (
              <path
                id={`arc-${data.id}`}
                class={{
                  'bam-chart__segment': true,
                  [`bam-chart__segment--is-${data.id}`]: true,
                  'is-active': data.id === this.activeItem,
                  'is-disabled': this.disabled,
                }}
                d={this.setArcDefinition([0, 0], data.radius, 0, data.angle, data.width, 8)}
                transform={`rotate(${data.startPosition})`}
                onClick={() => this.handleClick(data.id)}
                onMouseEnter={e => this.handleHover(e, data.id)}
                onMouseLeave={e => this.handleHover(e, data.id)}
              ></path>
            );
          })}
        </svg>
      </Host>
    );
  }
}
